// Comments.tsx
import { Tooltip, Button } from "antd";
import { FC, useMemo } from "react";
import { CommentsComponentModel } from "@services/api";
import cn from "classnames";
import classes from "./comments.module.css";

type Props = {
  comments: CommentsComponentModel[];
  /** новые сверху (по умолчанию true) */
  newestFirst?: boolean;
  /** группировать подряд идущие сообщения одного автора (по умолчанию true) */
  groupConsecutive?: boolean;
  /** компактный режим — показать только новейшую группу + кнопку "Посмотреть все" */
  compact?: boolean;
  /** обработчик клика по "Посмотреть все" (по умолчанию пустой) */
  onViewAll?: () => void;
  className?: string;
};

const ensureUtc = (iso: string) =>
  /[zZ]|[+\-]\d{2}:\d{2}$/.test(iso) ? iso : iso + "Z";

const formatRuDateTimeLocal = (isoUtc: string) => {
  const d = new Date(ensureUtc(isoUtc));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatTimeAgoRu = (isoUtc: string) => {
  const now = Date.now();
  const then = new Date(ensureUtc(isoUtc)).getTime();
  const diffSec = Math.max(1, Math.floor((now - then) / 1000));
  const rtf = new Intl.RelativeTimeFormat("ru", { numeric: "auto" });

  const MIN = 60, HOUR = 3600, DAY = 86400, WEEK = 604800, MONTH = 2592000, YEAR = 31536000;

  if (diffSec < MIN)  return rtf.format(-Math.round(diffSec), "second");
  if (diffSec < HOUR) return rtf.format(-Math.round(diffSec / MIN), "minute");
  if (diffSec < DAY)  return rtf.format(-Math.round(diffSec / HOUR), "hour");
  if (diffSec < WEEK) return rtf.format(-Math.round(diffSec / DAY), "day");
  if (diffSec < MONTH)return rtf.format(-Math.round(diffSec / WEEK), "week");
  if (diffSec < YEAR) return rtf.format(-Math.round(diffSec / MONTH), "month");
  return rtf.format(-Math.round(diffSec / YEAR), "year");
};

type Group = {
  author: string;
  isAgent: boolean;
  items: CommentsComponentModel[];
};

const norm = (s?: string) => (s ?? "").trim().toLowerCase();

const groupByConsecutiveAuthor = (list: CommentsComponentModel[]): Group[] => {
  const groups: Group[] = [];
  for (const c of list) {
    const author = c.author ?? "";
    const isAgent = norm(author) === "агент";
    const last = groups[groups.length - 1];
    if (last && norm(last.author) === norm(author)) {
      last.items.push(c);
    } else {
      groups.push({ author, isAgent, items: [c] });
    }
  }
  return groups;
};

export const Comments: FC<Props> = ({
  comments,
  newestFirst = true,
  groupConsecutive = true,
  compact = false,
  onViewAll,
  className
}) => {
  const ordered = useMemo(() => {
    const arr = [...comments];
    arr.sort((a, b) =>
      newestFirst
        ? new Date(ensureUtc(b.createdAt)).getTime() - new Date(ensureUtc(a.createdAt)).getTime()
        : new Date(ensureUtc(a.createdAt)).getTime() - new Date(ensureUtc(b.createdAt)).getTime()
    );
    return arr;
  }, [comments, newestFirst]);

  const groups = useMemo(
    () =>
      groupConsecutive
        ? groupByConsecutiveAuthor(ordered)
        : groupByConsecutiveAuthor(ordered.map(c => ({ ...c, author: "" }))),
    [ordered, groupConsecutive]
  );

  if (!groups.length) {
    return <div className={classes.empty}>Комментариев не найдено</div>;
  }

  const visibleGroups = compact ? groups.slice(0, 1) : groups;

  return (
    <div className={cn(classes.commentsList, className)}>
      {visibleGroups.map((g, gi) => (
        <div key={gi} className={cn(classes.commentGroup, g.isAgent ? classes.agent : classes.other)}>
          {g.author && <div className={classes.author}>{g.author}</div>}

          <div className={classes.bubble}>
            {g.items.map((item, i) => {
              const fullDate = formatRuDateTimeLocal(item.createdAt);
              const ago = formatTimeAgoRu(item.createdAt);
              return (
                <div key={i} className={classes.msgBlock}>
                  <div className={classes.message}>{item.message}</div>
                  <div className={classes.metaRow}>
                    <Tooltip title={ago} placement={g.isAgent ? "topRight" : "topLeft"}>
                      <span className={classes.metaTime} aria-label={ago}>{fullDate}</span>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {compact && groups.length > 1 && (
        <div className={classes.viewAllRow}>
          <Button type="link" onClick={onViewAll || (() => {})}>
            Посмотреть все
          </Button>
        </div>
      )}
    </div>
  );
};
